import { useMutation, useSubscription } from '@apollo/client/react';
import { PDFIcon } from '@slal/ui/icons';
import { FormProvider, useForm } from '@slal/ui/react-hook-form/core';
import { Heading } from '@slal/ui/typography';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Alert } from 'src/components/Alert';
import { useFunds } from 'src/context';
import { ButtonGroup, CardButton, ProgressStep, useAnalytics } from '~/components';
import { GenerateReportPDFRequest, OnGenerateReportPDFRequest } from '~/graphql';
import { useFormData } from '~/hooks';
import { SelectOption } from '~/types';
import { downloadFile, getPortfolioReportingPeriod, removeHtmlTags } from '~/utils';
import { PersonaliseCard } from '../application';
import { StyledForm } from '../styles';
import { strings } from './strings';
import { AlertWrapper, GenerateReportButton, StyledSpinner, StyledStatusMessage } from './styles';
import { DownloadReportStatus } from './types';

type GenerateReportPDFRequestResponse = {
    generateReportPDFRequest: {
        ID: string;
        Status: string;
    };
};

export const CustomiseReport = () => {
    const initialized = useRef(false);
    const [status, setStatus] = useState<DownloadReportStatus>('idle');
    const [subscriptionId, setSubscriptionId] = useState<string | null>(null);

    const { updateFormData, resetForm, formData } = useFormData();
    const [generateReportPDFRequest] = useMutation<GenerateReportPDFRequestResponse>(GenerateReportPDFRequest, {
        fetchPolicy: 'no-cache',
    });
    const { pushEvent } = useAnalytics();
    const hookForm = useForm({ mode: 'onChange' });
    const { clientName, reportingPeriod } = formData;
    const {
        firmName,
        adviserName,
        logoImageId,
        scale,
        welcomeNote,
        introductionNote,
        finalComment,
        includeWelcomeNote,
        includeIntroductionNote,
        includeFinalComment,
    } = hookForm.watch();
    const { selectedFunds, portfolioData, resetFunds } = useFunds();
    const isLoading = status === 'loading';
    const isCompleted = status === 'completed';

    const navigate = useNavigate();

    useEffect(() => {
        const values = hookForm.getValues();
        updateFormData(values);
    }, []);

    useEffect(() => {
        if (!Object.keys(formData).length) {
            navigate('/');
        }
    }, [formData, navigate]);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            pushEvent({
                eventCategory: 'Customise your report',
                eventAction: 'Pageload',
                eventLabel: 'Page 3',
            });
        }
    }, []);

    const subscriptionOptions = useMemo(
        () => ({
            variables: { ID: subscriptionId },
            skip: !subscriptionId, // Skip the subscription if subscriptionId is null
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onData: ({ data }: any) => {
                if (data.data.onGenerateReportPDFRequest.url) {
                    const { url } = data.data.onGenerateReportPDFRequest;
                    downloadFile(url, `test.pdf`);
                    pushEvent({
                        eventCategory: 'Customise your report',
                        eventAction: 'Message display',
                        eventLabel: 'Report downloaded. Check your downloads folder',
                    });
                    setStatus('completed');
                }
            },
            onError: (error: Error) => {
                console.warn(error);
                setStatus('error');
            },
        }),
        [subscriptionId]
    );

    useSubscription(OnGenerateReportPDFRequest, subscriptionOptions);

    const onValidSubmit = (values: Record<string, unknown>) => {
        updateFormData(values);
    };
    const maximumAvailablePeriod = getPortfolioReportingPeriod(selectedFunds);
    const reportingPeriodValue = (reportingPeriod as SelectOption)?.value ?? maximumAvailablePeriod;

    const input = {
        clientName: removeHtmlTags(clientName as string) ?? '',
        firmName: removeHtmlTags(firmName) ?? '',
        adviserName: removeHtmlTags(adviserName) ?? '',
        logoImageId: removeHtmlTags(logoImageId) ?? '',
        scale: removeHtmlTags(scale?.toString()) ?? '100',
        welcomeNote: includeWelcomeNote && welcomeNote?.length > 0 ? removeHtmlTags(welcomeNote) : '',
        introductionNote:
            includeIntroductionNote && introductionNote?.length > 0 ? removeHtmlTags(introductionNote) : '',
        finalComment: includeFinalComment && finalComment?.length > 0 ? removeHtmlTags(finalComment) : '',
        reportingPeriod: reportingPeriodValue,
        selectedFunds,
        ...portfolioData,
    };

    const handleDownloadReportClick = async () => {
        pushEvent({
            eventCategory: 'Customise your report',
            eventAction: 'Personalise',
            eventLabel: 'Download report',
        });
        pushEvent({
            eventCategory: 'Customise your report',
            eventAction: 'Message display',
            eventLabel: 'Please wait. The report may take up to a few minutes to download',
        });
        setStatus('loading');
        try {
            const { data } = await generateReportPDFRequest({
                variables: {
                    input,
                },
            });
            if (data?.generateReportPDFRequest?.ID) {
                setSubscriptionId(data.generateReportPDFRequest.ID);
            }
        } catch {
            setStatus('completed');
        }
    };

    const handleClick = () => {
        pushEvent({
            eventCategory: 'Customise your report',
            eventAction: 'Personalise',
            eventLabel: 'Start new report',
        });
        resetFunds();
        resetForm();
        navigate('/');
    };

    const handleBackButtonClick = () => {
        pushEvent({
            eventCategory: 'Customise your report',
            eventAction: 'Click',
            eventLabel: 'Back',
        });
        navigate('/reporting-period');
    };
    return (
        <FormProvider {...hookForm}>
            <StyledForm onSubmit={hookForm.handleSubmit(onValidSubmit)} id="customiseReportForm">
                <ProgressStep currentStep={3} totalSteps={3} />
                <Heading level={2}>{strings.customiseReport}</Heading>
                <PersonaliseCard />
                <AlertWrapper>
                    <Alert type="info">
                        Using this tool shouldn't be considered as Standard Life giving advice or making a
                        recommendation. It's important you make sure the content of the report satisfies your own
                        compliance requirements.
                    </Alert>
                </AlertWrapper>
                <ButtonGroup>
                    <GenerateReportButton
                        onClick={handleDownloadReportClick}
                        endIcon={
                            !isLoading ? (
                                <PDFIcon
                                    width={14}
                                    height={14}
                                    className="pdfIcon"
                                    role="presentation"
                                    aria-hidden="true"
                                />
                            ) : undefined
                        }
                        disabled={isLoading}
                        className="generate-report"
                    >
                        {isLoading ? <StyledSpinner title="Report" /> : strings.downloadReport}
                    </GenerateReportButton>
                    <CardButton role="button" variant="outline" onClick={handleClick} disabled={isLoading}>
                        {strings.startNewReport}
                    </CardButton>
                    <CardButton role="button" onClick={handleBackButtonClick} type="button" variant="outline">
                        Back
                    </CardButton>
                </ButtonGroup>
                {isLoading && <StyledStatusMessage>{strings.reportDownloadInProgressMessage}</StyledStatusMessage>}
                {isCompleted && <StyledStatusMessage>{strings.reportDownloadCompleteMessage}</StyledStatusMessage>}
            </StyledForm>
        </FormProvider>
    );
};
